import { API_BASE_URL, API_TIMEOUT } from '@constants'

/**
 * API Client Configuration
 */
class ApiClient {
	constructor(baseURL = API_BASE_URL) {
		this.baseURL = baseURL
		this.timeout = API_TIMEOUT
		this.defaultHeaders = {
			'Content-Type': 'application/json',
		}
	}

	/**
	 * Set authorization token
	 * @param {string} token - Auth token
	 */
	setAuthToken(token) {
		if (token) {
			this.defaultHeaders['Authorization'] = `Bearer ${token}`
		} else {
			delete this.defaultHeaders['Authorization']
		}
	}

	/**
	 * Make HTTP request
	 * @param {string} endpoint - API endpoint
	 * @param {Object} options - Request options
	 * @returns {Promise} Response data
	 */
	async request(endpoint, options = {}) {
		const url = `${this.baseURL}${endpoint}`
		const config = {
			...options,
			headers: {
				...this.defaultHeaders,
				...options.headers,
			},
		}

		try {
			const controller = new AbortController()
			const timeoutId = setTimeout(() => controller.abort(), this.timeout)

			const response = await fetch(url, {
				...config,
				signal: controller.signal,
			})

			clearTimeout(timeoutId)

			if (!response.ok) {
				const error = await response.json().catch(() => ({}))
				if (response.status === 401) {
					window.dispatchEvent(new CustomEvent('auth:unauthorized'))
				}
				const errorObj = new Error(error.message || `HTTP ${response.status}: ${response.statusText}`)
				errorObj.response = { status: response.status, data: error }
				throw errorObj
			}

			return await response.json()
		} catch (error) {
			if (error.name === 'AbortError') {
				throw new Error('Request timeout')
			}
			throw error
		}
	}

	/**
	 * GET request
	 */
	async get(endpoint, options = {}) {
		return this.request(endpoint, { ...options, method: 'GET' })
	}

	/**
	 * POST request
	 */
	async post(endpoint, data, options = {}) {
		return this.request(endpoint, {
			...options,
			method: 'POST',
			body: JSON.stringify(data),
		})
	}

	/**
	 * POST request with form data
	 * Used for endpoints that expect application/x-www-form-urlencoded
	 */
	async postForm(endpoint, data, options = {}) {
		const formBody = new URLSearchParams()
		Object.keys(data).forEach(key => {
			formBody.append(key, data[key])
		})

		return this.request(endpoint, {
			...options,
			method: 'POST',
			headers: {
				...options.headers,
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: formBody.toString(),
		})
	}

	/**
	 * Upload file with multipart/form-data
	 * @param {string} endpoint - API endpoint
	 * @param {File} file - File object to upload
	 * @param {Object} additionalData - Additional form fields
	 * @param {Function} onProgress - Progress callback (optional)
	 * @param {string} fieldName - Form field name for the file (default: 'file')
	 * @returns {Promise} Response data
	 */
	async uploadFile(endpoint, file, additionalData = {}, onProgress = null, fieldName = 'file') {
		const url = `${this.baseURL}${endpoint}`
		const formData = new FormData()

		// Add file with specified field name
		formData.append(fieldName, file)

		// Add additional fields
		Object.keys(additionalData).forEach(key => {
			formData.append(key, additionalData[key])
		})

		// Build headers (exclude Content-Type, let browser set it with boundary)
		const headers = { ...this.defaultHeaders }
		delete headers['Content-Type']

		try {
			// Use XMLHttpRequest for progress tracking if callback provided
			if (onProgress) {
				return new Promise((resolve, reject) => {
					const xhr = new XMLHttpRequest()

					xhr.upload.addEventListener('progress', (event) => {
						if (event.lengthComputable) {
							const percent = Math.round((event.loaded / event.total) * 100)
							onProgress(percent)
						}
					})

					xhr.addEventListener('load', () => {
						if (xhr.status >= 200 && xhr.status < 300) {
							try {
								resolve(JSON.parse(xhr.responseText))
							} catch {
								resolve({ status: 'success' })
							}
						} else {
							const error = new Error(`HTTP ${xhr.status}: ${xhr.statusText}`)
							try {
								error.response = { status: xhr.status, data: JSON.parse(xhr.responseText) }
							} catch {
								error.response = { status: xhr.status, data: {} }
							}
							reject(error)
						}
					})

					xhr.addEventListener('error', () => {
						reject(new Error('Network error during upload'))
					})

					xhr.addEventListener('timeout', () => {
						reject(new Error('Upload timeout'))
					})

					xhr.open('POST', url)
					xhr.timeout = this.timeout * 2 // Double timeout for file uploads

					// Set auth header
					if (headers['Authorization']) {
						xhr.setRequestHeader('Authorization', headers['Authorization'])
					}

					xhr.send(formData)
				})
			}

			// Standard fetch for uploads without progress tracking
			const controller = new AbortController()
			const timeoutId = setTimeout(() => controller.abort(), this.timeout * 2)

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: formData,
				signal: controller.signal,
			})

			clearTimeout(timeoutId)

			if (!response.ok) {
				const error = await response.json().catch(() => ({}))
				const errorObj = new Error(error.message || `HTTP ${response.status}: ${response.statusText}`)
				errorObj.response = { status: response.status, data: error }
				throw errorObj
			}

			return await response.json()
		} catch (error) {
			if (error.name === 'AbortError') {
				throw new Error('Upload timeout')
			}
			throw error
		}
	}

	/**
	 * PUT request
	 */
	async put(endpoint, data, options = {}) {
		return this.request(endpoint, {
			...options,
			method: 'PUT',
			body: JSON.stringify(data),
		})
	}

	/**
	 * PATCH request
	 */
	async patch(endpoint, data, options = {}) {
		console.log('PATCH request:', endpoint, 'Data:', data)
		const result = await this.request(endpoint, {
			...options,
			method: 'PATCH',
			body: JSON.stringify(data),
		})
		console.log('PATCH response:', result)
		return result
	}

	/**
	 * DELETE request
	 */
	async delete(endpoint, options = {}) {
		return this.request(endpoint, { ...options, method: 'DELETE' })
	}
}

// Export singleton instance
export const apiClient = new ApiClient()
export default apiClient
